/* MissionEditSheet — bottom-sheet modal for the creator to update a mission.
   Editable fields: title, description, urgency, address hint, photo (with
   client-side JPEG compression), and PayPal.me fundraising link.

   The fundraise link is optional and validated by the backend
   (only https://paypal.me/... allowed) so we can safely display it as a
   pass-through — ORIO never touches the money.
*/
import React, { useState } from "react";
import { toast } from "sonner";
import { X, HandCoins, Camera, Save } from "lucide-react";
import { api } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";

const URGENCIES = [
  { key: "critical", label: "Critica",  color: "#FF3B30" },
  { key: "high",     label: "Alta",     color: "#FF7A1A" },
  { key: "normal",   label: "Normale",  color: "#7A7A7A" },
];

export default function MissionEditSheet({ mission, onClose, onSaved }) {
  const [title, setTitle] = useState(mission.title || "");
  const [description, setDescription] = useState(mission.description || "");
  const [urgency, setUrgency] = useState(mission.urgency || "high");
  const [addressHint, setAddressHint] = useState(mission.address_hint || "");
  const [photo, setPhoto] = useState(mission.photo_url || "");
  const [fundraise, setFundraise] = useState(mission.fundraise_url || "");
  const [busy, setBusy] = useState(false);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) return toast.error("Foto troppo grande (max 25 MB)");
    try {
      const dataUrl = await compressImage(f, { maxSide: 1280, quality: 0.85 });
      setPhoto(dataUrl);
    } catch {
      toast.error("Impossibile leggere la foto");
    }
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        title: title.trim() || null,
        description: description.trim() || null,
        urgency,
        address_hint: addressHint.trim() || null,
        photo_url: photo || null,
        fundraise_url: fundraise.trim() || null,
      };
      await api.post(`/missions/v2/${mission.id}/update`, payload);
      toast.success("Missione aggiornata");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile salvare");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[2400] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="mission-edit-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-lg mx-0 sm:mx-3 rounded-t-[28px] sm:rounded-[28px] border border-[#F0EBE3] bg-white overflow-hidden"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
      >
        {/* Grabber */}
        <div className="sm:hidden pt-3 pb-1 flex justify-center">
          <span className="h-1 w-10 rounded-full bg-[#E5DECF]" />
        </div>

        {/* Header */}
        <div className="px-5 pt-2 pb-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#C25612]">Missione {mission.code}</div>
            <div className="font-display text-lg text-[#1A1A1A]">Modifica dettagli</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Chiudi"
            data-testid="mission-edit-close"
            className="h-9 w-9 rounded-full bg-[#F4EFE6] flex items-center justify-center"
          >
            <X className="h-4 w-4 text-[#6B6B6B]" />
          </button>
        </div>

        {/* Form */}
        <div className="px-5 py-3 max-h-[70vh] overflow-y-auto space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#7A7A7A]">Titolo</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              data-testid="mission-edit-title"
              className="mt-1 w-full h-11 rounded-2xl border border-[#E5DECF] px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF7A1A]/30"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#7A7A7A]">Descrizione</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={4}
              data-testid="mission-edit-desc"
              className="mt-1 w-full rounded-2xl border border-[#E5DECF] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF7A1A]/30"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#7A7A7A]">Urgenza</label>
            <div className="mt-1 flex gap-2">
              {URGENCIES.map((u) => (
                <button
                  key={u.key}
                  type="button"
                  onClick={() => setUrgency(u.key)}
                  data-testid={`mission-edit-urg-${u.key}`}
                  className="flex-1 h-11 rounded-2xl text-[13px] font-semibold border transition"
                  style={{
                    background: urgency === u.key ? u.color : "white",
                    color: urgency === u.key ? "white" : u.color,
                    borderColor: urgency === u.key ? u.color : "#E5DECF",
                  }}
                >
                  {u.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#7A7A7A]">Riferimento (via, punto)</label>
            <input
              value={addressHint}
              onChange={(e) => setAddressHint(e.target.value)}
              maxLength={200}
              data-testid="mission-edit-addr"
              placeholder="Vicino al parco, pizzeria X, ecc."
              className="mt-1 w-full h-11 rounded-2xl border border-[#E5DECF] px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF7A1A]/30"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#7A7A7A] flex items-center gap-1.5">
              <HandCoins className="h-3.5 w-3.5 text-[#FF7A1A]" /> Raccolta fondi (opzionale)
            </label>
            <input
              value={fundraise}
              onChange={(e) => setFundraise(e.target.value)}
              placeholder="https://paypal.me/iltuonome"
              data-testid="mission-edit-fundraise"
              className="mt-1 w-full h-11 rounded-2xl border border-[#E5DECF] px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF7A1A]/30"
            />
            <div className="text-[11px] text-[#9A9A9A] mt-1 leading-snug">
              ORIO non gestisce i soldi. Le donazioni vanno direttamente al tuo PayPal.
              Solo link PayPal.me sono accettati.
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-[#7A7A7A]">Foto</label>
            <div className="mt-1 rounded-2xl border border-dashed border-[#E5DECF] p-3 flex items-center gap-3">
              {photo ? (
                <img src={photo} alt="" className="h-16 w-16 rounded-xl object-cover" />
              ) : (
                <div className="h-16 w-16 rounded-xl bg-[#F4EFE6] flex items-center justify-center text-[#B5AE9E]">
                  <Camera className="h-6 w-6" />
                </div>
              )}
              <div className="flex-1">
                <label
                  className="inline-flex items-center gap-2 text-[13px] font-semibold text-white bg-[#1A1A1A] px-3 py-2 rounded-full cursor-pointer"
                  data-testid="mission-edit-photo-btn"
                >
                  <Camera className="h-3.5 w-3.5" />
                  {photo ? "Cambia foto" : "Aggiungi foto"}
                  <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
                </label>
                {photo && (
                  <button
                    onClick={() => setPhoto("")}
                    className="ml-2 text-[12px] text-[#B75E2A] underline underline-offset-2"
                  >
                    Rimuovi
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pt-3 pb-2 border-t border-[#F0EBE3] flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-full bg-[#F4EFE6] text-[#3A3A3A] text-[13px] font-semibold"
          >
            Annulla
          </button>
          <button
            onClick={save}
            disabled={busy || !title.trim()}
            data-testid="mission-edit-save"
            className="flex-1 h-11 rounded-full text-white text-[13px] font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" }}
          >
            <Save className="h-4 w-4" /> {busy ? "Salvo…" : "Salva modifiche"}
          </button>
        </div>
      </div>
    </div>
  );
}
